'use strict';

var uuidv4 = require('uuid/v4');
var session = require('../auth/models/session');

var DOCUMENT_VERSION = 2;
var STORAGE_PREFIX = 'nimrod:point-write-recovery:v2:';
var LOCK_PREFIX = 'nimrod:point-write-recovery-lock:v2:';
var UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
var ARCHIVE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
var MAX_ARCHIVE_RECEIPTS = 1000;
var REASSERT_TOKEN_HOLDER_KEY = '__pointWriteReassertTokens';
var ATTENDED_RECOVERY_POLICY = 'attended';

function recoveryError(message, code, cause) {
  var error = new Error(message);
  error.code = code || 'POINT_WRITE_RECOVERY_UNAVAILABLE';
  error.writeRecoveryUnavailable = true;
  if (cause) error.cause = cause;
  return error;
}

function isCleanupPersistenceError(error) {
  return !!(error && [
    'POINT_WRITE_RECOVERY_STORAGE_UNAVAILABLE',
    'POINT_WRITE_RECOVERY_LOCK_UNAVAILABLE',
    'POINT_WRITE_RECOVERY_PERSIST_FAILED',
    'POINT_WRITE_RECOVERY_VERIFY_FAILED',
    'POINT_WRITE_RECOVERY_LOCK_FAILED'
  ].indexOf(error.code) !== -1);
}

function isTerminalActorTransition(error) {
  return !!(error && [
    'POINT_WRITE_RECOVERY_ACTOR_UNAVAILABLE',
    'POINT_WRITE_RECOVERY_ACTOR_CHANGED'
  ].indexOf(error.code) !== -1);
}

function isAttendedStorageFallback(error) {
  return !!(error && [
    'POINT_WRITE_RECOVERY_STORAGE_UNAVAILABLE',
    'POINT_WRITE_RECOVERY_LOCK_UNAVAILABLE',
    'POINT_WRITE_RECOVERY_PERSIST_FAILED',
    'POINT_WRITE_RECOVERY_VERIFY_FAILED',
    'POINT_WRITE_RECOVERY_LOCK_FAILED',
    'POINT_WRITE_RECOVERY_CORRUPT',
    'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
  ].indexOf(error.code) !== -1);
}

function isAttendedRecord(record) {
  return !!(record && record.recoveryPolicy === ATTENDED_RECOVERY_POLICY);
}

function useMemoryRecovery(record) {
  if (record && isAttendedRecord(record)) record.recoveryStorage = 'memory';
  return record;
}

function canonicalValue(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(canonicalValue);
  var result = {};
  Object.keys(value).sort().forEach(function (key) {
    if (typeof value[key] !== 'undefined') result[key] = canonicalValue(value[key]);
  });
  return result;
}

function stableJson(value) {
  return JSON.stringify(canonicalValue(value));
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function recoveryIntentValue(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(recoveryIntentValue);
  var result = {};
  Object.keys(value).forEach(function (key) {
    if (key === 'writeOperationId' || key === 'writeOperationActorRid' ||
        key === 'expectedPointConfiguration') return;
    if (typeof value[key] !== 'undefined') result[key] = recoveryIntentValue(value[key]);
  });
  return result;
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.keys(value).forEach(function (key) { deepFreeze(value[key]); });
  return Object.freeze(value);
}

function normalizeActorRid(value) {
  if (value && typeof value === 'object') value = value['@rid'];
  var rid = String(value || '');
  if (rid && rid.charAt(0) !== '#') rid = '#' + rid;
  return /^#[0-9]+:[0-9]+$/.test(rid) ? rid : null;
}

function currentActorRid() {
  try {
    var user = session && typeof session.user === 'function' && session.user();
    return normalizeActorRid(user);
  } catch (_) {
    return null;
  }
}

function requireActorRid() {
  var actorRid = currentActorRid();
  if (!actorRid) {
    throw recoveryError(
      'Secure write recovery is unavailable until the signed-in user has loaded. Try again in a moment.',
      'POINT_WRITE_RECOVERY_ACTOR_UNAVAILABLE'
    );
  }
  return actorRid;
}

function requireActorMatch(expectedActorRid) {
  var actorRid = requireActorRid();
  if (actorRid !== expectedActorRid) {
    throw recoveryError(
      'The signed-in user changed after this write was prepared. Reconcile it under the original account before sending another write.',
      'POINT_WRITE_RECOVERY_ACTOR_CHANGED'
    );
  }
  return actorRid;
}

function durableStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (error) {
    throw recoveryError(
      'Secure write recovery storage is unavailable. Enable local site storage before sending a write.',
      'POINT_WRITE_RECOVERY_STORAGE_UNAVAILABLE',
      error
    );
  }
  throw recoveryError(
    'Secure write recovery storage is unavailable. Enable local site storage before sending a write.',
    'POINT_WRITE_RECOVERY_STORAGE_UNAVAILABLE'
  );
}

function lockManager() {
  var manager;
  try {
    manager = typeof window !== 'undefined' && window.navigator && window.navigator.locks;
  } catch (_) {}
  if (!manager || typeof manager.request !== 'function') {
    throw recoveryError(
      'Secure cross-tab write recovery locking is unavailable in this browser. Use a browser that supports the Web Locks API.',
      'POINT_WRITE_RECOVERY_LOCK_UNAVAILABLE'
    );
  }
  return manager;
}

function storageKey(actorRid) {
  return STORAGE_PREFIX + encodeURIComponent(actorRid);
}

function lockKey(actorRid) {
  return LOCK_PREFIX + encodeURIComponent(actorRid);
}

function emptyDocument(actorRid) {
  var document = {
    version: DOCUMENT_VERSION,
    actorRid: actorRid,
    operations: [],
    batches: [],
    operationReceipts: [],
    batchReceipts: [],
    integrityFingerprint: null
  };
  document.integrityFingerprint = documentIntegrityFingerprint(document);
  return document;
}

function documentIntegrityFingerprint(document) {
  var payload = {};
  Object.keys(document || {}).forEach(function (key) {
    if (key !== 'integrityFingerprint') payload[key] = document[key];
  });
  return stableJson(payload);
}

function validateTerminalTimestamp(record, message, code) {
  if (!record || record.terminalStatus !== 'success' ||
      typeof record.semanticEligible !== 'boolean' ||
      !UUID_V4_PATTERN.test(String(record.actionReceiptId || '')) ||
      typeof record.terminalAt !== 'number' ||
      !Number.isFinite(record.terminalAt) || record.terminalAt <= 0 ||
      (record.acknowledgedAt !== null &&
        (typeof record.acknowledgedAt !== 'number' ||
          !Number.isFinite(record.acknowledgedAt) || record.acknowledgedAt <= 0)) ||
      (record.supersededAt !== null &&
        (typeof record.supersededAt !== 'number' ||
          !Number.isFinite(record.supersededAt) || record.supersededAt <= 0))) {
    throw recoveryError(message, code);
  }
}

function validateOperationRecord(record, actorRid) {
  if (!record || typeof record !== 'object' || Array.isArray(record) ||
      record.actorRid !== actorRid || !UUID_V4_PATTERN.test(String(record.id || '')) ||
      typeof record.url !== 'string' || typeof record.fingerprint !== 'string' ||
      typeof record.intentFingerprint !== 'string' || typeof record.scope === 'undefined' ||
      typeof record.intent === 'undefined' || !record.body ||
      typeof record.body !== 'object' || Array.isArray(record.body) ||
      !Array.isArray(record.conflictScopes) ||
      stableJson(record.conflictScopes) !== stableJson(record.conflictScopes.slice().sort()) ||
      record.conflictScopes.some(function (scope, index) {
        return typeof scope !== 'string' || !scope || record.conflictScopes[index - 1] === scope;
      }) ||
      (record.conflictScopes.length
        ? typeof record.conflictGroup !== 'string' || !record.conflictGroup ||
          typeof record.receiptGroup !== 'string' || !record.receiptGroup
        : record.conflictGroup !== null || record.receiptGroup !== null) ||
      record.body.writeOperationId !== record.id ||
      record.body.writeOperationActorRid !== actorRid ||
      (record.attemptedAt !== null &&
        (typeof record.attemptedAt !== 'number' ||
          !Number.isFinite(record.attemptedAt) || record.attemptedAt <= 0)) ||
      (record.batchKey === null
        ? record.batchId !== null || record.rowKey !== null || record.phase !== null
        : typeof record.batchKey !== 'string' ||
          !UUID_V4_PATTERN.test(String(record.batchId || '')) ||
          typeof record.rowKey !== 'string' || !record.rowKey ||
          typeof record.phase !== 'string' || !record.phase)) {
    throw recoveryError(
      'Stored write recovery data is corrupt. Do not resend this write; reconcile the previous device outcome.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  var fingerprintBody = clone(record.body);
  delete fingerprintBody.writeOperationId;
  if (record.fingerprint !== operationFingerprint(actorRid, record.url, fingerprintBody)) {
    throw recoveryError(
      'Stored write recovery data does not match its request fingerprint. Do not resend this write.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  if (record.intentFingerprint !== operationIntentFingerprintFromParts(
    actorRid,
    record.scope,
    record.intent
  )) {
    throw recoveryError(
      'Stored write recovery intent does not match its request identity. Do not resend this write.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  return record;
}

function validateBatchRecord(record, actorRid) {
  if (!record || typeof record !== 'object' || Array.isArray(record) ||
      record.actorRid !== actorRid || !UUID_V4_PATTERN.test(String(record.id || '')) ||
      typeof record.batchKey !== 'string' || typeof record.kind !== 'string' ||
      typeof record.contextKey !== 'string' || typeof record.intentFingerprint !== 'string' ||
      typeof record.integrityFingerprint !== 'string' ||
      typeof record.intent === 'undefined' ||
      record.batchKey !== record.intentFingerprint ||
      (record.releaseAt !== null &&
        (typeof record.releaseAt !== 'number' ||
          !Number.isFinite(record.releaseAt) || record.releaseAt <= 0)) ||
      !record.rows || typeof record.rows !== 'object' || Array.isArray(record.rows) ||
      !Object.keys(record.rows).length) {
    throw recoveryError(
      'Stored mass-write recovery data is corrupt. Reconcile the previous batch before sending another.',
      'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
    );
  }
  if (record.intentFingerprint !== batchIntentFingerprint(actorRid, record)) {
    throw recoveryError(
      'Stored mass-write recovery intent does not match its durable ledger.',
      'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
    );
  }
  if (record.integrityFingerprint !== batchIntegrityFingerprint(actorRid, record)) {
    throw recoveryError(
      'Stored mass-write immutable deadline or row identity is corrupt.',
      'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
    );
  }
  Object.keys(record.rows).forEach(function (rowKey) {
    var row = record.rows[rowKey];
    if (!rowKey || !row || typeof row !== 'object' || Array.isArray(row) ||
        ['pending', 'success', 'expired'].indexOf(row.status) === -1 ||
        (row.warning !== null && typeof row.warning !== 'string') ||
        !Array.isArray(row.conflictScopes) ||
        stableJson(row.conflictScopes) !== stableJson(row.conflictScopes.slice().sort()) ||
        row.conflictScopes.some(function (scope, index) {
          return typeof scope !== 'string' || !scope || row.conflictScopes[index - 1] === scope;
        }) ||
        (row.conflictScopes.length
          ? typeof row.conflictGroup !== 'string' || !row.conflictGroup
          : row.conflictGroup !== null) ||
        typeof row.requestFingerprint !== 'string' ||
        !row.phases || typeof row.phases !== 'object' || Array.isArray(row.phases)) {
      throw recoveryError(
        'Stored mass-write row recovery data is corrupt.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
    Object.keys(row.phases).forEach(function (phaseName) {
      var phase = row.phases[phaseName];
      if (!phaseName || !phase || typeof phase !== 'object' || Array.isArray(phase) ||
          ['pending', 'success', 'expired'].indexOf(phase.status) === -1 ||
          (phase.operationId !== null && !UUID_V4_PATTERN.test(String(phase.operationId || '')))) {
        throw recoveryError(
          'Stored mass-write phase recovery data is corrupt.',
          'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
        );
      }
    });
    if (row.status !== 'pending' && !Object.keys(row.phases).length) {
      throw recoveryError(
        'Stored terminal mass-write row has no durable phase history.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
  });
  return record;
}

function validateDocumentRelationships(document, actorRid) {
  var operationsById = Object.create(null);
  var batchesByKey = Object.create(null);
  document.operations.forEach(function (operation) {
    if (operationsById[operation.id]) {
      throw recoveryError(
        'Stored write recovery data contains a duplicate operation identity.',
        'POINT_WRITE_RECOVERY_CORRUPT'
      );
    }
    operationsById[operation.id] = operation;
  });
  document.batches.forEach(function (batch) {
    if (batchesByKey[batch.batchKey]) {
      throw recoveryError(
        'Stored mass-write recovery data contains a duplicate batch identity.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
    batchesByKey[batch.batchKey] = batch;
  });

  document.operations.forEach(function (operation) {
    if (!operation.batchKey) {
      if (operation.rowKey || operation.phase) {
        throw recoveryError(
          'Stored standalone write recovery data contains an invalid batch reference.',
          'POINT_WRITE_RECOVERY_CORRUPT'
        );
      }
      return;
    }
    var batch = batchesByKey[operation.batchKey];
    var row = batch && batch.rows[operation.rowKey];
    var phase = row && row.phases[operation.phase];
    if (!batch || batch.id !== operation.batchId || batch.actorRid !== actorRid ||
        !row || row.status !== 'pending' ||
        !phase || phase.status !== 'pending' || phase.operationId !== operation.id) {
      throw recoveryError(
        'Stored mass-write recovery data contains an orphaned operation.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
  });

  document.batches.forEach(function (batch) {
    Object.keys(batch.rows).forEach(function (rowKey) {
      var row = batch.rows[rowKey];
      Object.keys(row.phases).forEach(function (phaseName) {
        var phase = row.phases[phaseName];
        if (row.status === 'success' || row.status === 'expired') {
          if (phase.status !== row.status || phase.operationId !== null) {
            throw recoveryError(
              'Stored completed mass-write row contains an unfinished phase.',
              'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
            );
          }
          return;
        }
        if (phase.status !== 'pending' ||
            (phase.operationId && !operationsById[phase.operationId])) {
          throw recoveryError(
            'Stored mass-write phase is missing its durable operation.',
            'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
          );
        }
      });
    });
  });
}

function parseDocument(raw, actorRid) {
  if (raw === null) return emptyDocument(actorRid);
  var document;
  try {
    document = JSON.parse(raw);
  } catch (error) {
    throw recoveryError(
      'Stored write recovery data cannot be read. Do not resend until the previous device outcome is reconciled.',
      'POINT_WRITE_RECOVERY_CORRUPT',
      error
    );
  }
  if (!document || document.version !== DOCUMENT_VERSION ||
      document.actorRid !== actorRid || !Array.isArray(document.operations) ||
      !Array.isArray(document.batches) ||
      !Array.isArray(document.operationReceipts) ||
      !Array.isArray(document.batchReceipts) ||
      typeof document.integrityFingerprint !== 'string') {
    throw recoveryError(
      'Stored write recovery data has an unsupported or corrupt format.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  if (document.integrityFingerprint !== documentIntegrityFingerprint(document)) {
    throw recoveryError(
      'Stored write recovery integrity verification failed. Do not resend until the previous device outcome is reconciled.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  document.operations.forEach(function (record) {
    validateOperationRecord(record, actorRid);
  });
  document.batches.forEach(function (record) {
    validateBatchRecord(record, actorRid);
  });
  document.operationReceipts.forEach(function (record) {
    validateOperationRecord(record, actorRid);
    validateTerminalTimestamp(
      record,
      'Stored write terminal receipt is corrupt.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
    if (record.batchKey !== null) {
      throw recoveryError(
        'Stored write terminal receipt has an invalid batch reference.',
        'POINT_WRITE_RECOVERY_CORRUPT'
      );
    }
  });
  document.batchReceipts.forEach(function (record) {
    validateBatchRecord(record, actorRid);
    validateTerminalTimestamp(
      record,
      'Stored mass-write terminal receipt is corrupt.',
      'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
    );
    if (Object.keys(record.rows).some(function (rowKey) {
      return record.rows[rowKey].status === 'pending';
    })) {
      throw recoveryError(
        'Stored mass-write terminal receipt contains a pending row.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
  });
  validateDocumentRelationships(document, actorRid);
  return document;
}

async function withLockedDocument(actorRid, mutator, options) {
  options = options || {};
  var target = durableStorage();
  var manager = lockManager();
  try {
    return await manager.request(lockKey(actorRid), { mode: 'exclusive' }, async function (lock) {
      if (!lock) {
        throw recoveryError(
          'Secure write recovery locking could not be acquired.',
          'POINT_WRITE_RECOVERY_LOCK_UNAVAILABLE'
        );
      }
      var key = storageKey(actorRid);
      var document = parseDocument(target.getItem(key), actorRid);
      var result = await mutator(document);
      if (options.readOnly !== true) {
        pruneTerminalReceiptArchives(document);
        document.integrityFingerprint = documentIntegrityFingerprint(document);
        var serialized = stableJson(document);
        try {
          target.setItem(key, serialized);
        } catch (error) {
          throw recoveryError(
            'Secure write recovery storage is full or unavailable. No device write was sent.',
            'POINT_WRITE_RECOVERY_PERSIST_FAILED',
            error
          );
        }
        var persisted;
        try {
          persisted = target.getItem(key);
        } catch (error) {
          throw recoveryError(
            'Secure write recovery storage could not be verified. No device write was sent.',
            'POINT_WRITE_RECOVERY_VERIFY_FAILED',
            error
          );
        }
        if (persisted !== serialized) {
          throw recoveryError(
            'Secure write recovery storage did not preserve the exact request. No device write was sent.',
            'POINT_WRITE_RECOVERY_VERIFY_FAILED'
          );
        }
        // Parse the readback as a second corruption/type check before allowing
        // the caller to reach a physical transport.
        parseDocument(persisted, actorRid);
      }
      return result;
    });
  } catch (error) {
    if (error && error.writeRecoveryUnavailable) throw error;
    throw recoveryError(
      'Secure write recovery locking failed. No device write was sent.',
      'POINT_WRITE_RECOVERY_LOCK_FAILED',
      error
    );
  }
}

function pruneTerminalReceiptArchives(document) {
  var activeEntries = document.operations.map(function (operation) {
    return { conflictScopes: operation.conflictScopes || [] };
  });
  document.batches.forEach(function (batch) {
    activeEntries = activeEntries.concat(batchConflictEntries(batch));
  });
  var cutoff = Date.now() - ARCHIVE_RETENTION_MS;
  function protectedByActive(receipt) {
    return receipt.conflictScopes
      ? recordOverlapsEntries(receipt, activeEntries)
      : batchOverlapsEntries(receipt, activeEntries);
  }
  function prune(receipts) {
    var archive = receipts.filter(function (receipt) {
      return (receipt.supersededAt !== null || receipt.semanticEligible === false ||
        receipt.acknowledgedAt !== null) &&
        !protectedByActive(receipt);
    }).sort(function (left, right) { return left.terminalAt - right.terminalAt; });
    var remove = archive.filter(function (receipt) {
      return receipt.terminalAt < cutoff;
    });
    var remainingArchiveCount = archive.length - remove.length;
    for (var index = 0;
      remainingArchiveCount > MAX_ARCHIVE_RECEIPTS && index < archive.length;
      index += 1) {
      if (remove.indexOf(archive[index]) === -1) {
        remove.push(archive[index]);
        remainingArchiveCount -= 1;
      }
    }
    return receipts.filter(function (receipt) { return remove.indexOf(receipt) === -1; });
  }
  document.operationReceipts = prune(document.operationReceipts);
  document.batchReceipts = prune(document.batchReceipts);
}

function operationFingerprint(actorRid, url, body) {
  return stableJson({
    version: DOCUMENT_VERSION,
    actorRid: actorRid,
    url: String(url || ''),
    body: body
  });
}

function operationIntentFingerprint(actorRid, url, key, body, options) {
  options = options || {};
  var hasStableScope = Object.prototype.hasOwnProperty.call(options, 'scope') &&
    options.scope !== null && typeof options.scope !== 'undefined' && options.scope !== '';
  return operationIntentFingerprintFromParts(
    actorRid,
    hasStableScope ? options.scope : {
      key: key,
      url: String(url || '')
    },
    Object.prototype.hasOwnProperty.call(options, 'intent') ? options.intent : body
  );
}

function operationIntentFingerprintFromParts(actorRid, scope, intent) {
  return stableJson({
    version: DOCUMENT_VERSION,
    actorRid: actorRid,
    scope: canonicalValue(scope),
    intent: recoveryIntentValue(intent)
  });
}

function publicOperation(record, restored) {
  var operation = clone(record);
  operation.restored = restored === true;
  operation.body = deepFreeze(operation.body);
  return operation;
}

function targetPointRidFromUrl(url) {
  var match = String(url || '').match(/\/api\/v1\/point\/([^/?#]+)\/write(?:\/|$)/);
  if (!match) return null;
  var rid;
  try {
    rid = decodeURIComponent(match[1]);
  } catch (_) {
    return null;
  }
  return normalizeActorRid(rid);
}

function pointRidsForOperation(operation) {
  if (!operation || !operation.body || !operation.url) {
    throw recoveryError(
      'The durable write target is missing. No device write was sent.',
      'POINT_WRITE_RECOVERY_TARGET_MISSING'
    );
  }
  var stagedValues = operation.body.stagedValues;
  var pointRids = Array.isArray(stagedValues) && stagedValues.length
    ? stagedValues.map(function (entry) {
      return normalizeActorRid(entry && entry.pointRid);
    })
    : [targetPointRidFromUrl(operation.url)];
  if (pointRids.some(function (rid) { return !rid; })) {
    throw recoveryError(
      'The durable write target cannot be recovered. No device write was sent.',
      'POINT_WRITE_RECOVERY_TARGET_MISSING'
    );
  }
  return pointRids.filter(function (rid, index) {
    return pointRids.indexOf(rid) === index;
  });
}

function pointRidForOperation(operation) {
  return pointRidsForOperation(operation)[0];
}

function pointConflictScope(pointRid) {
  var point = normalizeActorRid(pointRid);
  if (!point) {
    throw recoveryError(
      'A logical Point is required for write conflict recovery.',
      'POINT_WRITE_RECOVERY_SCOPE_INVALID'
    );
  }
  return ['point', point].join(':');
}

function reassertTokenKey(actorRid, receiptGroup, scope) {
  return stableJson({
    actorRid: actorRid,
    receiptGroup: receiptGroup,
    scope: receiptGroup ? null : scope
  });
}

function batchReassertTokenKey(actorRid, batchKey) {
  return stableJson({ actorRid: actorRid, batchKey: batchKey });
}

function reassertToken(holder, tokenKey) {
  var tokens = holder && holder[REASSERT_TOKEN_HOLDER_KEY];
  return tokens && tokens[tokenKey] || null;
}

function hasReassertActionToken(holder, actionReceiptId) {
  var tokens = holder && holder[REASSERT_TOKEN_HOLDER_KEY];
  return !!(tokens && Object.keys(tokens).some(function (tokenKey) {
    return tokens[tokenKey] === actionReceiptId;
  }));
}

function rememberReassertToken(holder, tokenKey, actionReceiptId) {
  if (!holder) return;
  if (!holder[REASSERT_TOKEN_HOLDER_KEY]) {
    Object.defineProperty(holder, REASSERT_TOKEN_HOLDER_KEY, {
      configurable: true,
      enumerable: false,
      writable: true,
      value: Object.create(null)
    });
  }
  holder[REASSERT_TOKEN_HOLDER_KEY][tokenKey] = actionReceiptId;
}

function clearReassertToken(holder, tokenKey, actionReceiptId) {
  var tokens = holder && holder[REASSERT_TOKEN_HOLDER_KEY];
  if (!tokens || (actionReceiptId && tokens[tokenKey] !== actionReceiptId)) return;
  delete tokens[tokenKey];
}

function operationMatches(record, operation) {
  return record && operation && record.id === operation.id &&
    record.actorRid === operation.actorRid && record.url === operation.url &&
    record.fingerprint === operation.fingerprint &&
    record.intentFingerprint === operation.intentFingerprint &&
    record.receiptGroup === operation.receiptGroup &&
    stableJson(record.body) === stableJson(operation.body) &&
    String(record.batchKey || '') === String(operation.batchKey || '') &&
    String(record.batchId || '') === String(operation.batchId || '') &&
    String(record.rowKey || '') === String(operation.rowKey || '') &&
    String(record.phase || '') === String(operation.phase || '');
}

function normalizeConflictScopes(scopes) {
  var normalized = (Array.isArray(scopes) ? scopes : [])
    .map(function (scope) { return String(scope || ''); })
    .filter(Boolean)
    .sort();
  if (normalized.some(function (scope, index) { return normalized[index - 1] === scope; })) {
    throw recoveryError(
      'Write conflict scopes must be unique.',
      'POINT_WRITE_RECOVERY_SCOPE_INVALID'
    );
  }
  return normalized;
}

function scopesOverlap(left, right) {
  return left.some(function (scope) { return right.indexOf(scope) !== -1; });
}

function conflictsWithEntries(record, entries) {
  var matching = entries.filter(function (entry) {
    return scopesOverlap(record.conflictScopes || [], entry.conflictScopes);
  });
  return matching.length && matching.some(function (entry) {
    return record.conflictGroup !== entry.conflictGroup;
  });
}

function batchConflictEntries(batch) {
  return Object.keys(batch.rows || {}).map(function (rowKey) {
    var row = batch.rows[rowKey];
    return {
      conflictScopes: row.conflictScopes || [],
      conflictGroup: row.conflictGroup
    };
  }).filter(function (entry) { return entry.conflictScopes.length; });
}

function recordOverlapsEntries(record, entries) {
  return entries.some(function (entry) {
    return scopesOverlap(record.conflictScopes || [], entry.conflictScopes) ||
      !(record.conflictScopes || []).length && !entry.conflictScopes.length &&
        typeof entry.scope !== 'undefined' &&
        stableJson(record.scope) === stableJson(entry.scope);
  });
}

function batchOverlapsEntries(batch, entries) {
  return batchConflictEntries(batch).some(function (rowEntry) {
    return entries.some(function (entry) {
      return scopesOverlap(rowEntry.conflictScopes, entry.conflictScopes);
    });
  });
}

function retireOverlappingTerminalReceipts(document, entries) {
  entries = entries.filter(function (entry) {
    return entry.conflictScopes.length || typeof entry.scope !== 'undefined';
  });
  if (!entries.length) return;
  var supersededAt = Date.now();
  document.operationReceipts.forEach(function (receipt) {
    if (receipt.supersededAt === null && recordOverlapsEntries(receipt, entries)) {
      receipt.supersededAt = supersededAt;
    }
  });
  document.batchReceipts.forEach(function (receipt) {
    if (receipt.supersededAt === null && batchOverlapsEntries(receipt, entries)) {
      receipt.supersededAt = supersededAt;
    }
  });
}

function terminalizeBatch(document, batch, options) {
  options = options || {};
  if (Object.keys(batch.rows).some(function (rowKey) {
    return batch.rows[rowKey].status === 'pending';
  })) {
    throw recoveryError(
      'A mass-operation batch cannot become terminal while a row is pending.',
      'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
    );
  }
  document.operations = document.operations.filter(function (operation) {
    return operation.batchKey !== batch.batchKey || operation.batchId !== batch.id;
  });
  if (options.preserveReceipt !== false) {
    if (options.retireOverlapping === true) {
      retireOverlappingTerminalReceipts(document, batchConflictEntries(batch));
      var supersededAt = Date.now();
      document.batchReceipts.forEach(function (receipt) {
        if (receipt.batchKey === batch.batchKey && receipt.supersededAt === null) {
          receipt.supersededAt = supersededAt;
        }
      });
    }
    var duplicate = document.batchReceipts.some(function (receipt) {
      return receipt.batchKey === batch.batchKey && receipt.id === batch.id;
    });
    if (!duplicate) {
      document.batchReceipts.push(Object.assign({}, clone(batch), {
        terminalStatus: 'success',
        terminalAt: Date.now(),
        acknowledgedAt: null,
        supersededAt: null,
        semanticEligible: options.semanticEligible !== false,
        actionReceiptId: batch.id
      }));
    }
  }
  document.batches = document.batches.filter(function (candidate) {
    return candidate !== batch;
  });
}

function supersedeAttendedRecovery(document, entries, options) {
  options = options || {};
  entries = (entries || []).filter(function (entry) {
    return entry && ((entry.conflictScopes || []).length ||
      typeof entry.scope !== 'undefined');
  });
  var exactBatchKey = options.batchKey || null;
  var supersededAt = Date.now();

  retireOverlappingTerminalReceipts(document, entries);
  if (exactBatchKey) {
    document.batchReceipts.forEach(function (receipt) {
      if (receipt.batchKey === exactBatchKey && receipt.supersededAt === null) {
        receipt.supersededAt = supersededAt;
      }
    });
  }

  // A new attended action is deliberately a new device command. Remove old
  // standalone recovery identities that touch the same logical Point so they
  // cannot be replayed by a delayed tab after this action is prepared.
  document.operations = document.operations.filter(function (operation) {
    return operation.batchKey !== null || !recordOverlapsEntries(operation, entries);
  });

  document.batches.slice().forEach(function (batch) {
    var removeWholeBatch = exactBatchKey && batch.batchKey === exactBatchKey;
    var removedRows = Object.keys(batch.rows).filter(function (rowKey) {
      return removeWholeBatch || recordOverlapsEntries(batch.rows[rowKey], entries);
    });
    if (!removedRows.length) return;

    document.operations = document.operations.filter(function (operation) {
      return operation.batchKey !== batch.batchKey || operation.batchId !== batch.id ||
        removedRows.indexOf(operation.rowKey) === -1;
    });
    removedRows.forEach(function (rowKey) {
      delete batch.rows[rowKey];
    });

    var remainingRows = Object.keys(batch.rows);
    if (!remainingRows.length) {
      document.batches = document.batches.filter(function (candidate) {
        return candidate !== batch;
      });
      return;
    }

    batch.updatedAt = Date.now();
    batch.integrityFingerprint = batchIntegrityFingerprint(batch.actorRid, batch);
    if (remainingRows.every(function (rowKey) {
      return batch.rows[rowKey].status !== 'pending';
    })) {
      terminalizeBatch(document, batch, {
        preserveReceipt: true,
        retireOverlapping: false,
        semanticEligible: false
      });
    }
  });
}

function supersedeConflictingUnattempted(document, entries) {
  entries = entries.filter(function (entry) { return entry.conflictScopes.length; });
  if (!entries.length) return;
  var conflictingStandalone = document.operations.filter(function (operation) {
    return operation.batchKey === null && conflictsWithEntries(operation, entries);
  });
  var affectedRows = [];
  var lockedBatches = [];
  document.batches.forEach(function (batch) {
    var matchingRows = [];
    Object.keys(batch.rows).forEach(function (rowKey) {
      var row = batch.rows[rowKey];
      if (conflictsWithEntries(row, entries)) matchingRows.push({
        batch: batch, rowKey: rowKey, row: row
      });
    });
    if (!matchingRows.length) return;
    var hasTerminalRow = Object.keys(batch.rows).some(function (rowKey) {
      return batch.rows[rowKey].status !== 'pending';
    });
    var hasAttemptedPhase = document.operations.some(function (operation) {
      return operation.batchKey === batch.batchKey && operation.batchId === batch.id &&
        operation.attemptedAt !== null;
    });
    if (hasTerminalRow || hasAttemptedPhase || matchingRows.some(function (entry) {
      return entry.row.status !== 'pending';
    })) {
      lockedBatches.push(batch);
    } else {
      affectedRows = affectedRows.concat(matchingRows);
    }
  });
  var conflictingBatchOperations = document.operations.filter(function (operation) {
    return operation.batchKey !== null && affectedRows.some(function (affected) {
      return affected.batch.batchKey === operation.batchKey &&
        affected.batch.id === operation.batchId && affected.rowKey === operation.rowKey;
    });
  });
  var conflicts = conflictingStandalone.concat(conflictingBatchOperations);
  if (lockedBatches.length ||
      conflicts.some(function (operation) { return operation.attemptedAt !== null; })) {
    throw recoveryError(
      'Another write touching this logical Point was already attempted. Reconcile it before starting this action.',
      'POINT_WRITE_RECOVERY_RECONCILIATION_REQUIRED'
    );
  }
  document.operations = document.operations.filter(function (operation) {
    return conflicts.indexOf(operation) === -1;
  });
  var affectedBatches = [];
  affectedRows.forEach(function (affected) {
    if (!affectedBatches.some(function (entry) { return entry.batch === affected.batch; })) {
      affectedBatches.push({
        batch: affected.batch,
        preserveReceipt: Object.keys(affected.batch.rows).some(function (rowKey) {
          return affected.batch.rows[rowKey].status !== 'pending';
        })
      });
    }
    affected.row.status = 'expired';
    affected.row.warning = 'Superseded before transport by a newer write action.';
    Object.keys(affected.row.phases).forEach(function (phaseName) {
      affected.row.phases[phaseName].status = 'expired';
      affected.row.phases[phaseName].operationId = null;
    });
    affected.batch.updatedAt = Date.now();
  });
  affectedBatches.forEach(function (affected) {
    var batch = affected.batch;
    if (Object.keys(batch.rows).every(function (rowKey) {
      return batch.rows[rowKey].status !== 'pending';
    })) {
      terminalizeBatch(document, batch, {
        preserveReceipt: affected.preserveReceipt,
        retireOverlapping: false,
        semanticEligible: false
      });
    }
  });
}

function assertNoConflictingActive(document, entries) {
  var snapshot = clone(document);
  try {
    supersedeConflictingUnattempted(snapshot, entries);
  } catch (error) {
    throw error;
  }
  if (stableJson(snapshot.operations) !== stableJson(document.operations) ||
      stableJson(snapshot.batches) !== stableJson(document.batches)) {
    throw recoveryError(
      'A newer write touching this logical Point is already pending. Finish or reconcile it before retrying the older action.',
      'POINT_WRITE_RECOVERY_SUPERSEDED'
    );
  }
}

function findExactOperation(document, operation) {
  var matches = document.operations.filter(function (record) {
    return operationMatches(record, operation);
  });
  if (matches.length > 1) {
    throw recoveryError(
      'More than one exact durable write recovery record exists.',
      'POINT_WRITE_RECOVERY_AMBIGUOUS'
    );
  }
  return matches[0] || null;
}

function findTerminalOperationReceipt(document, operation) {
  var matches = document.operationReceipts.filter(function (record) {
    return operationMatches(record, operation);
  });
  if (matches.length > 1) {
    throw recoveryError(
      'More than one terminal receipt exists for this write.',
      'POINT_WRITE_RECOVERY_CORRUPT'
    );
  }
  return matches[0] || null;
}

function terminalAppliedError(receipt) {
  var error = recoveryError(
    'This exact write was already confirmed. No new device request was sent.',
    'POINT_WRITE_ALREADY_TERMINAL'
  );
  error.writeApplied = true;
  error.writeActionComplete = receipt.semanticEligible === true;
  error.terminalReceipt = publicOperation(receipt, true);
  return error;
}

function terminalBatchOperationError(document, operation) {
  if (!operation || !operation.batchKey || !operation.batchId) return null;
  var batch = document.batches.concat(document.batchReceipts).find(function (candidate) {
    return candidate.batchKey === operation.batchKey && candidate.id === operation.batchId;
  });
  var row = batch && batch.rows[operation.rowKey];
  if (!row || row.status === 'pending') return null;
  var error = recoveryError(
    'This mass-operation row was already terminalized. No new device request was sent.',
    'POINT_WRITE_BATCH_ALREADY_TERMINAL'
  );
  error.writeApplied = row.status === 'success';
  error.writeExpired = row.status === 'expired';
  error.batch = publicBatch(batch, true);
  return error;
}

async function prepare(holder, key, url, body, options) {
  holder = holder || {};
  options = options || {};
  key = String(key || 'default');
  var actorRid = requireActorRid();
  var attended = options.recoveryPolicy === ATTENDED_RECOVERY_POLICY;
  if (holder[key] && holder[key].actorRid && holder[key].actorRid !== actorRid) {
    if (attended) {
      delete holder[key];
    } else {
      throw recoveryError(
        'The signed-in user changed while this write was unresolved. Reconcile it under the original account before retrying.',
        'POINT_WRITE_RECOVERY_ACTOR_CHANGED'
      );
    }
  }
  var frozenBody = clone(body || {});
  delete frozenBody.writeOperationId;
  delete frozenBody.writeOperationActorRid;
  var exactBody = Object.assign({}, frozenBody, {
    writeOperationActorRid: actorRid
  });
  var hasStableScope = Object.prototype.hasOwnProperty.call(options, 'scope') &&
    options.scope !== null && typeof options.scope !== 'undefined' && options.scope !== '';
  var recoveryScope = hasStableScope ? canonicalValue(options.scope) : {
    key: key,
    url: String(url || '')
  };
  var recoveryIntent = recoveryIntentValue(
    Object.prototype.hasOwnProperty.call(options, 'intent') ? options.intent : exactBody
  );
  var conflictScopes = normalizeConflictScopes(options.conflictScopes);
  var conflictGroup = conflictScopes.length ? String(options.conflictGroup || '') : null;
  var receiptGroup = conflictScopes.length
    ? String(options.receiptGroup || conflictGroup || '')
    : null;
  if (conflictScopes.length && !conflictGroup) {
    throw recoveryError(
      'A write conflict group is required when logical Point scopes are supplied.',
      'POINT_WRITE_RECOVERY_SCOPE_INVALID'
    );
  }
  var fingerprint = operationFingerprint(actorRid, url, exactBody);
  var intentFingerprint = operationIntentFingerprint(
    actorRid,
    url,
    key,
    exactBody,
    options
  );
  var tokenKey = reassertTokenKey(actorRid, receiptGroup, recoveryScope);
  var presentedReassertToken = reassertToken(holder, tokenKey);
  var heldOperation = holder[key] && holder[key].id ? holder[key] : null;
  function createOperationRecord() {
    var id = uuidv4().toLowerCase();
    var requestBody = Object.assign({}, exactBody, { writeOperationId: id });
    var record = {
      id: id,
      actorRid: actorRid,
      url: String(url || ''),
      fingerprint: fingerprint,
      intentFingerprint: intentFingerprint,
      scope: recoveryScope,
      intent: recoveryIntent,
      conflictScopes: conflictScopes,
      conflictGroup: conflictGroup,
      receiptGroup: receiptGroup,
      body: requestBody,
      createdAt: Date.now(),
      attemptedAt: null,
      batchKey: null,
      batchId: null,
      rowKey: null,
      phase: null
    };
    if (attended) {
      record.recoveryPolicy = ATTENDED_RECOVERY_POLICY;
      record.recoveryStorage = 'durable';
    }
    return record;
  }
  var recordAndState;
  try {
    recordAndState = await withLockedDocument(actorRid, function (document) {
      var conflictEntry = {
        conflictScopes: conflictScopes,
        conflictGroup: conflictGroup,
        scope: recoveryScope
      };
      if (attended) {
        supersedeAttendedRecovery(document, [conflictEntry]);
        var attendedRecord = createOperationRecord();
        document.operations.push(attendedRecord);
        return { record: clone(attendedRecord), restored: false };
      }
    if (heldOperation) {
      var heldReceipt = findTerminalOperationReceipt(document, heldOperation);
      var heldReceiptMatchesCurrent = heldReceipt &&
        heldReceipt.fingerprint === fingerprint &&
        heldReceipt.intentFingerprint === intentFingerprint &&
        stableJson(heldReceipt.scope) === stableJson(recoveryScope) &&
        stableJson(heldReceipt.conflictScopes) === stableJson(conflictScopes) &&
        heldReceipt.conflictGroup === conflictGroup &&
        heldReceipt.receiptGroup === receiptGroup;
      if (heldReceiptMatchesCurrent) {
        return {
          terminalReceipt: clone(heldReceipt),
          exactHolder: true,
          offerReassertToken: heldReceipt.semanticEligible === true
        };
      }
    }
    var matches = document.operations.filter(function (record) {
      return record.conflictGroup === conflictGroup &&
        ((record.fingerprint === fingerprint &&
          stableJson(record.scope) === stableJson(recoveryScope)) ||
        record.intentFingerprint === intentFingerprint);
    });
    if (matches.length > 1) {
      throw recoveryError(
        'Multiple unresolved writes match this action. Reconcile them before sending another.',
        'POINT_WRITE_RECOVERY_AMBIGUOUS'
      );
    }
    if (matches.length) {
      if (matches[0].batchKey) {
        throw recoveryError(
          'This write belongs to an unfinished mass operation. Reopen that batch to reconcile it.',
          'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
        );
      }
      var exactCurrentRequest = matches[0].fingerprint === fingerprint &&
        stableJson(matches[0].scope) === stableJson(recoveryScope) &&
        stableJson(matches[0].conflictScopes) === stableJson(conflictScopes) &&
        matches[0].receiptGroup === receiptGroup;
      if (matches[0].attemptedAt !== null || exactCurrentRequest) {
        return { record: clone(matches[0]), restored: true };
      }
      // The logical action is unchanged, but its unattempted physical target
      // or configuration moved. Replace it atomically before fetch. Attempted
      // generations always replay their persisted exact request above.
      document.operations = document.operations.filter(function (record) {
        return record !== matches[0];
      });
    }
    var terminalMatches = document.operationReceipts.filter(function (record) {
      return record.supersededAt === null && record.semanticEligible === true &&
        record.conflictGroup === conflictGroup && record.receiptGroup === receiptGroup &&
        record.fingerprint === fingerprint &&
        stableJson(record.scope) === stableJson(recoveryScope) &&
        stableJson(record.conflictScopes) === stableJson(conflictScopes);
    });
    if (terminalMatches.length > 1) {
      throw recoveryError(
        'Multiple terminal receipts match this write action.',
        'POINT_WRITE_RECOVERY_CORRUPT'
      );
    }
    if (terminalMatches.length &&
        presentedReassertToken !== terminalMatches[0].actionReceiptId &&
        !hasReassertActionToken(holder, terminalMatches[0].actionReceiptId)) {
      // A stale tab that merely rediscovered an old receipt must never cancel
      // a newer pending intent. It receives the same no-op proof and a local
      // token; only a later explicit save from that holder may reassert.
      assertNoConflictingActive(document, [conflictEntry]);
      var acknowledgedAt = Date.now();
      document.operationReceipts.forEach(function (receipt) {
        if (receipt.actionReceiptId === terminalMatches[0].actionReceiptId) {
          receipt.acknowledgedAt = acknowledgedAt;
        }
      });
      return {
        terminalReceipt: clone(terminalMatches[0]),
        offerReassertToken: true
      };
    }
    // A matching local action token denotes a later explicit save. Normal new
    // intents also serialize here, but a no-token terminal lookup above never
    // mutates a newer pending action.
    supersedeConflictingUnattempted(document, [conflictEntry]);
    var sameScope = document.operations.filter(function (record) {
      return record.batchKey === null &&
        stableJson(record.scope) === stableJson(recoveryScope);
    });
    var attemptedConflict = sameScope.find(function (record) {
      return record.attemptedAt !== null;
    });
    if (attemptedConflict) {
      throw recoveryError(
        'A different write in this logical scope was already attempted. Reconcile it before changing the requested value.',
        'POINT_WRITE_RECOVERY_RECONCILIATION_REQUIRED'
      );
    }
    if (sameScope.length) {
      // These records are durably marked unattempted, so a newer user intent
      // may supersede them before any caller crosses the fetch boundary.
      document.operations = document.operations.filter(function (record) {
        return sameScope.indexOf(record) === -1;
      });
    }
      var record = createOperationRecord();
      document.operations.push(record);
      return { record: clone(record), restored: false };
    });
  } catch (error) {
    if (!attended || !isAttendedStorageFallback(error)) throw error;
    var memoryRecord = useMemoryRecovery(createOperationRecord());
    recordAndState = { record: memoryRecord, restored: false };
  }
  if (recordAndState.terminalReceipt) {
    if (recordAndState.exactHolder && holder[key] === heldOperation) delete holder[key];
    if (recordAndState.offerReassertToken) {
      rememberReassertToken(
        holder,
        tokenKey,
        recordAndState.terminalReceipt.actionReceiptId
      );
    }
    throw terminalAppliedError(recordAndState.terminalReceipt);
  }
  if (attended) clearReassertToken(holder, tokenKey);
  var operation = publicOperation(recordAndState.record, recordAndState.restored);
  holder[key] = operation;
  return operation;
}

async function assertDurable(operation) {
  if (!operation || !operation.actorRid) {
    throw recoveryError('A durable write operation is required.', 'POINT_WRITE_RECOVERY_MISSING');
  }
  requireActorMatch(operation.actorRid);
  if (operation.recoveryStorage === 'memory' && isAttendedRecord(operation)) return operation;
  try {
    return await withLockedDocument(operation.actorRid, function (document) {
      var record = findExactOperation(document, operation);
      if (!record) {
        var receipt = findTerminalOperationReceipt(document, operation);
        if (receipt) throw terminalAppliedError(receipt);
        var batchTerminal = terminalBatchOperationError(document, operation);
        if (batchTerminal) throw batchTerminal;
        throw recoveryError(
          'The exact durable write recovery record is missing. No device write was sent.',
          'POINT_WRITE_RECOVERY_MISSING'
        );
      }
      return publicOperation(record, true);
    }, { readOnly: true });
  } catch (error) {
    if (!isAttendedRecord(operation) || !isAttendedStorageFallback(error)) throw error;
    return useMemoryRecovery(operation);
  }
}

async function markAttempted(operation) {
  if (!operation || !operation.actorRid) {
    throw recoveryError('A durable write operation is required.', 'POINT_WRITE_RECOVERY_MISSING');
  }
  requireActorMatch(operation.actorRid);
  if (operation.recoveryStorage === 'memory' && isAttendedRecord(operation)) {
    if (operation.attemptedAt === null) operation.attemptedAt = Date.now();
    return operation;
  }
  var record;
  try {
    record = await withLockedDocument(operation.actorRid, function (document) {
      var durable = findExactOperation(document, operation);
      if (!durable) {
        var receipt = findTerminalOperationReceipt(document, operation);
        if (receipt) throw terminalAppliedError(receipt);
        var batchTerminal = terminalBatchOperationError(document, operation);
        if (batchTerminal) throw batchTerminal;
        throw recoveryError(
          'The exact durable write recovery record is missing. No device write was sent.',
          'POINT_WRITE_RECOVERY_MISSING'
        );
      }
      if (durable.attemptedAt === null) durable.attemptedAt = Date.now();
      return clone(durable);
    });
    operation.attemptedAt = record.attemptedAt;
  } catch (error) {
    if (!isAttendedRecord(operation) || !isAttendedStorageFallback(error)) throw error;
    useMemoryRecovery(operation);
    if (operation.attemptedAt === null) operation.attemptedAt = Date.now();
  }
  return operation;
}

function writeOperationMetadata(error) {
  if (!error) return null;
  return error.writeOperation ||
    error.response && error.response.writeOperation ||
    error.response && error.response.data && error.response.data.writeOperation ||
    error.event && error.event.writeOperation ||
    error.event && {
      retrySafe: error.event.retrySafe,
      deviceDisposition: error.event.deviceDisposition
    } || null;
}

function isProvenRetrySafe(error) {
  var metadata = writeOperationMetadata(error);
  return !!(metadata && metadata.retrySafe === true &&
    String(metadata.deviceDisposition || '').toLowerCase() === 'not-invoked');
}

function clearBatchPhaseForOperation(document, operation) {
  if (!operation.batchKey) return;
  var batch = document.batches.find(function (candidate) {
    return candidate.batchKey === operation.batchKey && candidate.id === operation.batchId;
  });
  if (!batch || !batch.rows[operation.rowKey]) return;
  var phase = batch.rows[operation.rowKey].phases &&
    batch.rows[operation.rowKey].phases[operation.phase];
  if (phase && phase.operationId === operation.id) {
    phase.operationId = null;
    phase.status = 'pending';
  }
}

async function removeDurableOperation(holder, key, operation) {
  requireActorMatch(operation.actorRid);
  if (!(operation.recoveryStorage === 'memory' && isAttendedRecord(operation))) {
    try {
      await withLockedDocument(operation.actorRid, function (document) {
        var durable = findExactOperation(document, operation);
        if (!durable) {
          // complete() and the proven-not-invoked fail() branch are terminal-proof
          // APIs. Another tab may already have performed this exact cleanup.
          return;
        }
        document.operations = document.operations.filter(function (record) {
          return record !== durable;
        });
        clearBatchPhaseForOperation(document, operation);
      });
    } catch (error) {
      if (!isAttendedRecord(operation) || !isAttendedStorageFallback(error)) throw error;
      useMemoryRecovery(operation);
    }
  }
  var memoryBatch = operation && operation.__memoryBatch;
  if (memoryBatch) {
    var remembered = memoryBatchOperations(memoryBatch);
    delete remembered[operation.id];
    var row = memoryBatch.rows[operation.rowKey];
    var phase = row && row.phases && row.phases[operation.phase];
    if (phase && phase.operationId === operation.id) {
      phase.operationId = null;
      phase.status = 'pending';
    }
  }
  if (holder && holder[key] === operation) delete holder[key];
}

async function complete(holder, key, operation) {
  if (!operation || !operation.actorRid) {
    throw recoveryError('A durable write operation is required.', 'POINT_WRITE_RECOVERY_MISSING');
  }
  if (operation.batchKey) {
    throw recoveryError(
      'Mass-operation writes are completed through their durable batch ledger.',
      'POINT_WRITE_BATCH_COMPLETION_REQUIRED'
    );
  }
  return completeMany(holder, [{ key: key, operation: operation }]);
}

async function completeMany(holder, entries) {
  entries = Array.isArray(entries) ? entries : [];
  if (!entries.length) return;
  var actorRid = entries[0] && entries[0].operation && entries[0].operation.actorRid;
  if (!actorRid || entries.some(function (entry) {
    return !entry || !entry.operation || entry.operation.actorRid !== actorRid ||
      entry.operation.batchKey;
  })) {
    throw recoveryError(
      'Atomic write cleanup requires standalone operations from one signed-in user.',
      'POINT_WRITE_RECOVERY_MISSING'
    );
  }
  try {
    requireActorMatch(actorRid);
  } catch (error) {
    if (isTerminalActorTransition(error)) {
      return { terminal: true, cleanupVerified: false };
    }
    throw error;
  }
  var ids = Object.create(null);
  entries.forEach(function (entry) {
    if (ids[entry.operation.id]) {
      throw recoveryError(
        'Atomic write cleanup contains a duplicate operation.',
        'POINT_WRITE_RECOVERY_AMBIGUOUS'
      );
    }
    ids[entry.operation.id] = true;
  });
  var verified = false;
  var attended = entries.every(function (entry) {
    return isAttendedRecord(entry.operation);
  });
  var memoryOnly = attended && entries.every(function (entry) {
    return entry.operation.recoveryStorage === 'memory';
  });
  var actionReceiptId = uuidv4().toLowerCase();
  try {
    if (!memoryOnly) await withLockedDocument(actorRid, function (document) {
      var durable = [];
      var receipts = [];
      entries.forEach(function (entry) {
        var record = findExactOperation(document, entry.operation);
        var receipt = findTerminalOperationReceipt(document, entry.operation);
        if (record) durable.push(record);
        if (receipt) receipts.push(receipt);
      });
      if (durable.length) {
        if (receipts.some(function (receipt) { return receipt.supersededAt !== null; })) {
          throw recoveryError(
            'A completed phase was superseded before the remaining write phases finished. Reconcile the aggregate action before retrying it.',
            'POINT_WRITE_RECOVERY_RECONCILIATION_REQUIRED'
          );
        }
        retireOverlappingTerminalReceipts(document, durable.map(function (record) {
          return {
            conflictScopes: record.conflictScopes,
            conflictGroup: record.conflictGroup,
            scope: record.scope
          };
        }));
        receipts.forEach(function (receipt) {
          receipt.terminalAt = Date.now();
          receipt.acknowledgedAt = null;
          receipt.supersededAt = null;
          receipt.semanticEligible = true;
          receipt.actionReceiptId = actionReceiptId;
        });
      }
      durable.forEach(function (record) {
        var receipt = Object.assign({}, clone(record), {
          terminalStatus: 'success',
          terminalAt: Date.now(),
          acknowledgedAt: null,
          supersededAt: null,
          semanticEligible: true,
          actionReceiptId: actionReceiptId
        });
        document.operationReceipts.push(receipt);
        receipts.push(receipt);
      });
      document.operations = document.operations.filter(function (record) {
        return durable.indexOf(record) === -1;
      });
      return receipts.map(function (receipt) { return receipt.id; });
    });
    verified = !memoryOnly;
  } catch (error) {
    // The physical outcome is already authoritatively confirmed. Preserve an
    // active record when the transition did not commit, or a terminal receipt
    // when it committed but readback failed; never downgrade this to a
    // retryable UI failure.
    if (!isCleanupPersistenceError(error) &&
        !(attended && isAttendedStorageFallback(error))) throw error;
    if (attended) entries.forEach(function (entry) {
      useMemoryRecovery(entry.operation);
    });
  }
  if (verified || attended) {
    entries.forEach(function (entry) {
      if (holder && holder[entry.key] === entry.operation) delete holder[entry.key];
      clearReassertToken(holder, reassertTokenKey(
        actorRid,
        entry.operation.receiptGroup,
        entry.operation.scope
      ));
    });
  }
  return { terminal: true, cleanupVerified: verified };
}

async function expireMany(holder, entries, confirmedEntries) {
  entries = Array.isArray(entries) ? entries : [];
  confirmedEntries = Array.isArray(confirmedEntries) ? confirmedEntries : [];
  if (!entries.length) return;
  var actorRid = entries[0] && entries[0].operation && entries[0].operation.actorRid;
  if (!actorRid || entries.some(function (entry) {
    return !entry || !entry.operation || entry.operation.actorRid !== actorRid ||
      entry.operation.batchKey;
  })) {
    throw recoveryError(
      'Expired write cleanup requires standalone operations from one signed-in user.',
      'POINT_WRITE_RECOVERY_MISSING'
    );
  }
  try {
    requireActorMatch(actorRid);
  } catch (error) {
    if (isTerminalActorTransition(error)) {
      return { terminal: true, cleanupVerified: false };
    }
    throw error;
  }
  var confirmedIds = Object.create(null);
  confirmedEntries.forEach(function (entry) {
    var operation = entry && (entry.operation || entry);
    if (operation && operation.id) confirmedIds[operation.id] = true;
  });
  var actionReceiptId = uuidv4().toLowerCase();
  var verified = false;
  var attended = entries.every(function (entry) {
    return isAttendedRecord(entry.operation);
  });
  var memoryOnly = attended && entries.every(function (entry) {
    return entry.operation.recoveryStorage === 'memory';
  });
  try {
    if (!memoryOnly) await withLockedDocument(actorRid, function (document) {
      var durable = entries.map(function (entry) {
        var record = findExactOperation(document, entry.operation);
        if (!record) return null;
        if (record.attemptedAt !== null && !confirmedIds[record.id]) {
          throw recoveryError(
            'An attempted write is still unresolved and cannot be expired.',
            'POINT_WRITE_RECOVERY_EXPIRED_UNRESOLVED'
          );
        }
        return record;
      }).filter(Boolean);
      var confirmedDurable = durable.filter(function (record) {
        return confirmedIds[record.id];
      });
      if (confirmedDurable.length) {
        retireOverlappingTerminalReceipts(document, confirmedDurable.map(function (record) {
          return {
            conflictScopes: record.conflictScopes,
            conflictGroup: record.conflictGroup,
            scope: record.scope
          };
        }));
        confirmedDurable.forEach(function (record) {
          document.operationReceipts.push(Object.assign({}, clone(record), {
            terminalStatus: 'success',
            terminalAt: Date.now(),
            acknowledgedAt: null,
            supersededAt: null,
            semanticEligible: false,
            actionReceiptId: actionReceiptId
          }));
        });
      }
      document.operations = document.operations.filter(function (record) {
        return durable.indexOf(record) === -1;
      });
    });
    verified = !memoryOnly;
  } catch (error) {
    if (!isCleanupPersistenceError(error) &&
        !(attended && isAttendedStorageFallback(error))) throw error;
    if (attended) entries.forEach(function (entry) {
      useMemoryRecovery(entry.operation);
    });
  }
  if (verified || attended) {
    entries.forEach(function (entry) {
      if (holder && holder[entry.key] === entry.operation) delete holder[entry.key];
    });
  }
  return { terminal: true, cleanupVerified: verified };
}

async function fail(holder, key, operation, error) {
  if (isProvenRetrySafe(error)) {
    await removeDurableOperation(holder, key, operation);
    return true;
  }
  await assertDurable(operation);
  return false;
}

function sortedUniqueRowKeys(rows) {
  var keys = (rows || []).map(function (row) {
    return String(row && (row.rowKey || row) || '');
  }).filter(Boolean).sort();
  if (!keys.length || keys.some(function (key, index) { return keys[index - 1] === key; })) {
    throw recoveryError(
      'A mass operation requires a unique non-empty selected row set.',
      'POINT_WRITE_BATCH_ROWS_INVALID'
    );
  }
  return keys;
}

function batchIntentFingerprint(actorRid, options) {
  return stableJson({
    version: DOCUMENT_VERSION,
    actorRid: actorRid,
    kind: String(options.kind || ''),
    contextKey: String(options.contextKey || ''),
    intent: options.intent
  });
}

function batchIntegrityFingerprint(actorRid, batch) {
  var immutableRows = {};
  Object.keys(batch.rows || {}).sort().forEach(function (rowKey) {
    immutableRows[rowKey] = {
      conflictScopes: batch.rows[rowKey].conflictScopes,
      conflictGroup: batch.rows[rowKey].conflictGroup,
      requestFingerprint: batch.rows[rowKey].requestFingerprint
    };
  });
  return stableJson({
    version: DOCUMENT_VERSION,
    actorRid: actorRid,
    id: batch.id,
    batchKey: batch.batchKey,
    releaseAt: batch.releaseAt,
    rows: immutableRows
  });
}

function publicBatch(record, restored) {
  var batch = clone(record);
  batch.restored = restored === true;
  return batch;
}

async function prepareBatch(holder, key, options) {
  holder = holder || {};
  options = options || {};
  key = String(key || 'batch');
  var actorRid = requireActorRid();
  var attended = options.recoveryPolicy === ATTENDED_RECOVERY_POLICY;
  if (holder[key] && holder[key].actorRid && holder[key].actorRid !== actorRid) {
    if (attended) {
      delete holder[key];
    } else {
      throw recoveryError(
        'The signed-in user changed while this mass operation was unresolved. Reconcile it under the original account before retrying.',
        'POINT_WRITE_RECOVERY_ACTOR_CHANGED'
      );
    }
  }
  var rowKeys = sortedUniqueRowKeys(options.rows);
  var conflictEntries = rowKeys.map(function (rowKey) {
    var scopes = normalizeConflictScopes(
      options.rowConflictScopes && options.rowConflictScopes[rowKey]
    );
    var group = scopes.length
      ? String(options.rowConflictGroups && options.rowConflictGroups[rowKey] || '')
      : null;
    if (scopes.length && !group) {
      throw recoveryError(
        'Each mass-operation conflict scope requires a durable row conflict group.',
        'POINT_WRITE_RECOVERY_SCOPE_INVALID'
      );
    }
    return {
      rowKey: rowKey,
      conflictScopes: scopes,
      conflictGroup: group,
      requestFingerprint: stableJson(options.rowRequestIdentities &&
        Object.prototype.hasOwnProperty.call(options.rowRequestIdentities, rowKey)
        ? options.rowRequestIdentities[rowKey]
        : null)
    };
  });
  conflictEntries.forEach(function (entry, index) {
    var overlap = conflictEntries.slice(index + 1).find(function (candidate) {
      return scopesOverlap(entry.conflictScopes, candidate.conflictScopes);
    });
    if (overlap) {
      throw recoveryError(
        'Two selected mass-operation rows resolve to the same physical Point. Correct the write topology or change the selection.',
        'POINT_WRITE_BATCH_ROWS_CONFLICT'
      );
    }
  });
  var intentFingerprint = batchIntentFingerprint(actorRid, options);
  var batchKey = intentFingerprint;
  var tokenKey = batchReassertTokenKey(actorRid, batchKey);
  var presentedReassertToken = reassertToken(holder, tokenKey);
  function createBatchRecord() {
    var rows = {};
    conflictEntries.forEach(function (entry) {
      rows[entry.rowKey] = {
        status: 'pending',
        warning: null,
        conflictScopes: entry.conflictScopes,
        conflictGroup: entry.conflictGroup,
        requestFingerprint: entry.requestFingerprint,
        phases: {}
      };
    });
    var batch = {
      id: uuidv4().toLowerCase(),
      batchKey: batchKey,
      actorRid: actorRid,
      kind: String(options.kind || ''),
      contextKey: String(options.contextKey || ''),
      intentFingerprint: intentFingerprint,
      intent: clone(options.intent),
      releaseAt: Number.isFinite(Number(options.releaseAt)) ? Number(options.releaseAt) : null,
      rows: rows,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    if (attended) {
      batch.recoveryPolicy = ATTENDED_RECOVERY_POLICY;
      batch.recoveryStorage = 'durable';
    }
    batch.integrityFingerprint = batchIntegrityFingerprint(actorRid, batch);
    return batch;
  }
  var state;
  try {
    state = await withLockedDocument(actorRid, function (document) {
    if (attended) {
      supersedeAttendedRecovery(document, conflictEntries, { batchKey: batchKey });
    }
    var existing = document.batches.find(function (batch) {
      return batch.batchKey === batchKey;
    });
    if (existing) {
      if (stableJson(Object.keys(existing.rows).sort()) !== stableJson(rowKeys)) {
        throw recoveryError(
          'An unfinished mass operation uses a different selected row set. Reopen that selection to reconcile it before starting another.',
          'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
        );
      }
      var batchAttempted = document.operations.some(function (operation) {
        return operation.batchKey === existing.batchKey && operation.batchId === existing.id &&
          operation.attemptedAt !== null;
      });
      var batchHasTerminalRow = Object.keys(existing.rows).some(function (rowKey) {
        return existing.rows[rowKey].status !== 'pending';
      });
      conflictEntries.forEach(function (entry) {
        var row = existing.rows[entry.rowKey];
        if (stableJson(row.conflictScopes) === stableJson(entry.conflictScopes) &&
            row.conflictGroup === entry.conflictGroup &&
            row.requestFingerprint === entry.requestFingerprint) return;
        if (!batchAttempted && !batchHasTerminalRow && row.status === 'pending') {
          row.conflictScopes = entry.conflictScopes;
          row.conflictGroup = entry.conflictGroup;
          row.requestFingerprint = entry.requestFingerprint;
        }
      });
      existing.integrityFingerprint = batchIntegrityFingerprint(actorRid, existing);
      existing.updatedAt = Date.now();
      return { batch: clone(existing), restored: true };
    }
    var terminal = document.batchReceipts.find(function (batch) {
      return batch.batchKey === batchKey && batch.supersededAt === null &&
        batch.semanticEligible === true && conflictEntries.every(function (entry) {
          var row = batch.rows[entry.rowKey];
          return row && stableJson(row.conflictScopes) === stableJson(entry.conflictScopes) &&
            row.conflictGroup === entry.conflictGroup &&
            row.requestFingerprint === entry.requestFingerprint;
        });
    });
    if (terminal) {
      if (stableJson(Object.keys(terminal.rows).sort()) !== stableJson(rowKeys)) {
        throw recoveryError(
          'A terminal mass-operation receipt uses a different selected row set.',
          'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
        );
      }
      if (presentedReassertToken !== terminal.actionReceiptId) {
        assertNoConflictingActive(document, conflictEntries);
        var newerOverlappingBatch = document.batches.some(function (batch) {
          return batch.batchKey !== batchKey && batch.kind === String(options.kind || '') &&
            batch.contextKey === String(options.contextKey || '') &&
            Object.keys(batch.rows).some(function (rowKey) {
              return rowKeys.indexOf(rowKey) !== -1;
            });
        });
        if (newerOverlappingBatch) {
          throw recoveryError(
            'A newer overlapping mass operation is pending. Finish it before retrying this terminal action.',
            'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
          );
        }
        terminal.acknowledgedAt = terminal.acknowledgedAt || Date.now();
        return {
          batch: clone(terminal),
          restored: true,
          terminalReceipt: true,
          offerReassertToken: true
        };
      }
    }

    supersedeConflictingUnattempted(document, conflictEntries);

    var overlapping = document.batches.filter(function (batch) {
      if (batch.kind !== String(options.kind || '') ||
          batch.contextKey !== String(options.contextKey || '')) return false;
      return Object.keys(batch.rows).some(function (rowKey) {
        return rowKeys.indexOf(rowKey) !== -1;
      });
    });
    if (overlapping.length) {
      var attemptedOperation = document.operations.find(function (operation) {
        return operation.attemptedAt !== null && overlapping.some(function (batch) {
          return batch.batchKey === operation.batchKey && batch.id === operation.batchId;
        });
      });
      if (attemptedOperation) {
        var conflict = recoveryError(
          'An unfinished mass operation overlaps this selection and contains an attempted write. Reopen the original selection to reconcile it.',
          'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
        );
        conflict.batch = publicBatch(overlapping.find(function (batch) {
          return batch.batchKey === attemptedOperation.batchKey;
        }), true);
        throw conflict;
      }
      // Pending requests are all durably unattempted. Preserve any prior row
      // outcomes in a terminal receipt so a stale exact generation cannot
      // recreate already-confirmed physical writes.
      overlapping.forEach(function (overlap) {
        var preserveReceipt = Object.keys(overlap.rows).some(function (rowKey) {
          return overlap.rows[rowKey].status !== 'pending';
        });
        Object.keys(overlap.rows).forEach(function (rowKey) {
          var row = overlap.rows[rowKey];
          if (row.status !== 'pending') return;
          row.status = 'expired';
          row.warning = 'Superseded before transport by a newer mass-write action.';
          Object.keys(row.phases).forEach(function (phaseName) {
            row.phases[phaseName].status = 'expired';
            row.phases[phaseName].operationId = null;
          });
        });
        overlap.updatedAt = Date.now();
        terminalizeBatch(document, overlap, {
          preserveReceipt: preserveReceipt,
          retireOverlapping: false,
          semanticEligible: false
        });
      });
    }

    var batch = createBatchRecord();
    document.batches.push(batch);
    return { batch: clone(batch), restored: false };
    });
  } catch (error) {
    if (!attended || !isAttendedStorageFallback(error)) throw error;
    state = { batch: useMemoryRecovery(createBatchRecord()), restored: false };
  }
  var batch = publicBatch(state.batch, state.restored);
  batch.terminalReceipt = state.terminalReceipt === true;
  if (state.offerReassertToken) {
    rememberReassertToken(holder, tokenKey, batch.actionReceiptId);
  }
  if (attended) clearReassertToken(holder, tokenKey);
  holder[key] = batch;
  return batch;
}

function operationSpecRecord(actorRid, batch, spec) {
  var body = clone(spec.body || {});
  delete body.writeOperationId;
  delete body.writeOperationActorRid;
  body.writeOperationActorRid = actorRid;
  var key = String(spec.key || (spec.rowKey + ':' + spec.phase));
  var fingerprint = operationFingerprint(actorRid, spec.url, body);
  var intentOptions = {};
  if (Object.prototype.hasOwnProperty.call(spec, 'scope')) {
    intentOptions.scope = spec.scope;
  }
  if (Object.prototype.hasOwnProperty.call(spec, 'intent')) {
    intentOptions.intent = spec.intent;
  }
  var intentFingerprint = operationIntentFingerprint(
    actorRid,
    spec.url,
    key,
    body,
    intentOptions
  );
  var hasStableScope = Object.prototype.hasOwnProperty.call(spec, 'scope') &&
    spec.scope !== null && typeof spec.scope !== 'undefined' && spec.scope !== '';
  var recoveryScope = hasStableScope ? canonicalValue(spec.scope) : {
    key: key,
    url: String(spec.url || '')
  };
  var recoveryIntent = recoveryIntentValue(
    Object.prototype.hasOwnProperty.call(spec, 'intent') ? spec.intent : body
  );
  var durableRow = batch.rows[String(spec.rowKey || '')];
  var conflictScopes = normalizeConflictScopes(
    Object.prototype.hasOwnProperty.call(spec, 'conflictScopes')
      ? spec.conflictScopes
      : durableRow && durableRow.conflictScopes
  );
  var conflictGroup = conflictScopes.length
    ? String(Object.prototype.hasOwnProperty.call(spec, 'conflictGroup')
      ? spec.conflictGroup
      : durableRow && durableRow.conflictGroup || '')
    : null;
  if (!durableRow || stableJson(conflictScopes) !== stableJson(durableRow.conflictScopes) ||
      conflictGroup !== durableRow.conflictGroup) {
    throw recoveryError(
      'The mass-operation phase conflict identity does not match its durable row.',
      'POINT_WRITE_BATCH_PHASE_INVALID'
    );
  }
  var id = uuidv4().toLowerCase();
  var record = {
    id: id,
    actorRid: actorRid,
    url: String(spec.url || ''),
    fingerprint: fingerprint,
    intentFingerprint: intentFingerprint,
    scope: recoveryScope,
    intent: recoveryIntent,
    conflictScopes: conflictScopes,
    conflictGroup: conflictGroup,
    receiptGroup: conflictGroup,
    body: Object.assign({}, body, { writeOperationId: id }),
    createdAt: Date.now(),
    attemptedAt: null,
    batchKey: batch.batchKey,
    batchId: batch.id,
    rowKey: String(spec.rowKey || ''),
    phase: String(spec.phase || '')
  };
  if (isAttendedRecord(batch)) {
    record.recoveryPolicy = ATTENDED_RECOVERY_POLICY;
    record.recoveryStorage = batch.recoveryStorage || 'durable';
  }
  return record;
}

function memoryBatchOperations(batch) {
  if (!batch.__memoryOperations) {
    Object.defineProperty(batch, '__memoryOperations', {
      configurable: true,
      enumerable: false,
      writable: true,
      value: Object.create(null)
    });
  }
  return batch.__memoryOperations;
}

function rememberBatchOperations(batch, operations) {
  var remembered = memoryBatchOperations(batch);
  (operations || []).forEach(function (operation) {
    if (!operation.__memoryBatch) {
      Object.defineProperty(operation, '__memoryBatch', {
        configurable: true,
        enumerable: false,
        writable: true,
        value: batch
      });
    }
    remembered[operation.id] = operation;
  });
}

function seedMemoryBatchOperations(batch, specs) {
  var remembered = memoryBatchOperations(batch);
  return specs.map(function (spec) {
    validateBatchSpec(batch, spec);
    var row = batch.rows[String(spec.rowKey)];
    row.phases = row.phases || {};
    var phaseName = String(spec.phase);
    var phase = row.phases[phaseName] || { status: 'pending', operationId: null };
    var expected = useMemoryRecovery(operationSpecRecord(batch.actorRid, batch, spec));
    var operation = phase.operationId && remembered[phase.operationId];
    if (phase.operationId && !operation) {
      throw recoveryError(
        'The in-memory operation for this mass-operation phase is missing.',
        'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
      );
    }
    if (operation) {
      var exactSpec = operation.url === expected.url &&
        operation.fingerprint === expected.fingerprint &&
        operation.intentFingerprint === expected.intentFingerprint &&
        stableJson(operation.scope) === stableJson(expected.scope) &&
        stableJson(operation.conflictScopes) === stableJson(expected.conflictScopes) &&
        operation.conflictGroup === expected.conflictGroup;
      if (operation.attemptedAt === null && !exactSpec) {
        delete remembered[operation.id];
        operation = expected;
      }
    } else {
      operation = expected;
    }
    var publicRecord = publicOperation(operation, !!phase.operationId);
    rememberBatchOperations(batch, [publicRecord]);
    phase.operationId = publicRecord.id;
    phase.status = 'pending';
    row.phases[phaseName] = phase;
    return publicRecord;
  });
}

function validateBatchSpec(batch, spec) {
  var rowKey = String(spec && spec.rowKey || '');
  var phase = String(spec && spec.phase || '');
  if (rowKey && phase && batch.rows[rowKey] && batch.rows[rowKey].status !== 'pending') {
    var rowStatus = batch.rows[rowKey].status;
    var terminal = recoveryError(
      'This mass-operation row is already terminal. No new device request was sent.',
      'POINT_WRITE_BATCH_ALREADY_TERMINAL'
    );
    terminal.writeApplied = rowStatus === 'success';
    terminal.writeExpired = rowStatus === 'expired';
    throw terminal;
  }
  if (!rowKey || !phase || !batch.rows[rowKey]) {
    throw recoveryError(
      'The mass-operation phase does not belong to a pending durable row.',
      'POINT_WRITE_BATCH_PHASE_INVALID'
    );
  }
}

async function seedBatchOperations(batch, specs) {
  if (!batch || !batch.actorRid || !batch.batchKey) {
    throw recoveryError('A durable mass-operation ledger is required.', 'POINT_WRITE_BATCH_MISSING');
  }
  requireActorMatch(batch.actorRid);
  specs = Array.isArray(specs) ? specs : [];
  var phaseKeys = Object.create(null);
  specs.forEach(function (spec) {
    var phaseKey = String(spec && spec.rowKey || '') + ':' + String(spec && spec.phase || '');
    if (phaseKeys[phaseKey]) {
      throw recoveryError(
        'A mass operation cannot seed the same row phase more than once.',
        'POINT_WRITE_BATCH_PHASE_INVALID'
      );
    }
    phaseKeys[phaseKey] = true;
  });
  if (batch.recoveryStorage === 'memory' && isAttendedRecord(batch)) {
    return seedMemoryBatchOperations(batch, specs);
  }
  var records;
  try {
    records = await withLockedDocument(batch.actorRid, function (document) {
    var durableBatch = document.batches.find(function (candidate) {
      return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
    });
    if (!durableBatch) {
      var terminalBatch = document.batchReceipts.find(function (candidate) {
        return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
      });
      if (terminalBatch) {
        if (!specs.length) return [];
        var terminalError = recoveryError(
          'This exact mass operation is already terminal. No new device request was sent.',
          'POINT_WRITE_BATCH_ALREADY_TERMINAL'
        );
        var requestedStatuses = specs.map(function (spec) {
          return terminalBatch.rows[String(spec.rowKey || '')] &&
            terminalBatch.rows[String(spec.rowKey || '')].status;
        });
        terminalError.writeApplied = requestedStatuses.length > 0 &&
          requestedStatuses.every(function (status) { return status === 'success'; });
        terminalError.writeExpired = requestedStatuses.some(function (status) {
          return status === 'expired';
        });
        terminalError.batch = publicBatch(terminalBatch, true);
        throw terminalError;
      }
      throw recoveryError('The durable mass-operation ledger is missing.', 'POINT_WRITE_BATCH_MISSING');
    }
    var created = [];
    var batchHasAttemptedOperation = document.operations.some(function (operation) {
      return operation.batchKey === durableBatch.batchKey &&
        operation.batchId === durableBatch.id && operation.attemptedAt !== null;
    }) || Object.keys(durableBatch.rows).some(function (rowKey) {
      return durableBatch.rows[rowKey].status !== 'pending';
    });
    specs.forEach(function (spec) {
      validateBatchSpec(durableBatch, spec);
      var row = durableBatch.rows[String(spec.rowKey)];
      row.phases = row.phases || {};
      var phase = row.phases[String(spec.phase)] || { status: 'pending', operationId: null };
      var expected = operationSpecRecord(batch.actorRid, durableBatch, spec);
      var operation = phase.operationId && document.operations.find(function (candidate) {
        return candidate.id === phase.operationId;
      });
      if (phase.operationId && !operation) {
        throw recoveryError(
          'The durable operation for this mass-operation phase is missing.',
          'POINT_WRITE_BATCH_RECOVERY_CORRUPT'
        );
      }
      if (operation) {
        if (operation.batchKey !== durableBatch.batchKey ||
            operation.batchId !== durableBatch.id ||
            operation.rowKey !== expected.rowKey || operation.phase !== expected.phase) {
          throw recoveryError(
            'An unresolved write from another action conflicts with this mass-operation phase.',
            'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
          );
        }
        var exactSpec = operation.url === expected.url &&
          operation.fingerprint === expected.fingerprint &&
          operation.intentFingerprint === expected.intentFingerprint &&
          stableJson(operation.scope) === stableJson(expected.scope) &&
          stableJson(operation.conflictScopes) === stableJson(expected.conflictScopes) &&
          operation.conflictGroup === expected.conflictGroup;
        if (operation.attemptedAt === null && !batchHasAttemptedOperation && !exactSpec) {
          // A pre-seeded request that was never marked attempted is proven not
          // to have reached fetch. Refresh it to the latest target/configuration
          // before this row starts, with a new idempotency identity.
          document.operations = document.operations.filter(function (candidate) {
            return candidate !== operation;
          });
          operation = expected;
          document.operations.push(operation);
        }
      } else {
        var specRequestFingerprint = stableJson(
          Object.prototype.hasOwnProperty.call(spec, 'rowRequestIdentity')
            ? spec.rowRequestIdentity
            : null
        );
        if (batchHasAttemptedOperation &&
            specRequestFingerprint !== row.requestFingerprint) {
          throw recoveryError(
            'The physical request identity changed after this mass operation started. Abandon or reconcile the original batch before using the new topology.',
            'POINT_WRITE_BATCH_RECONCILIATION_REQUIRED'
          );
        }
        operation = expected;
        document.operations.push(operation);
      }
      phase.operationId = operation.id;
      phase.status = 'pending';
      row.phases[String(spec.phase)] = phase;
      created.push(clone(operation));
    });
    durableBatch.updatedAt = Date.now();
    return created;
    });
  } catch (error) {
    if (!isAttendedRecord(batch) || !isAttendedStorageFallback(error)) throw error;
    useMemoryRecovery(batch);
    return seedMemoryBatchOperations(batch, specs);
  }
  var publicRecords = records.map(function (record) { return publicOperation(record, true); });
  rememberBatchOperations(batch, publicRecords);
  return publicRecords;
}

function forgetMemoryBatchRow(batch, rowKey) {
  var remembered = memoryBatchOperations(batch);
  Object.keys(remembered).forEach(function (id) {
    if (remembered[id].rowKey === rowKey) delete remembered[id];
  });
}

function markMemoryBatchRowSuccess(holder, key, batch, rowKey, warning) {
  var row = batch.rows[rowKey];
  if (!row) {
    throw recoveryError('The in-memory mass-operation row is missing.', 'POINT_WRITE_BATCH_ROW_MISSING');
  }
  row.status = 'success';
  row.warning = warning ? String(warning) : null;
  Object.keys(row.phases || {}).forEach(function (phaseName) {
    row.phases[phaseName].status = 'success';
    row.phases[phaseName].operationId = null;
  });
  forgetMemoryBatchRow(batch, rowKey);
  batch.updatedAt = Date.now();
  var completed = Object.keys(batch.rows).every(function (candidateRowKey) {
    return batch.rows[candidateRowKey].status !== 'pending';
  });
  if (completed && holder && holder[key] === batch) delete holder[key];
  return completed;
}

function markMemoryBatchRowExpired(holder, key, batch, rowKey, confirmedOperations, warning) {
  var row = batch.rows[rowKey];
  if (!row || row.status !== 'pending') {
    throw recoveryError(
      'The pending in-memory mass-operation row is missing during expiry cleanup.',
      'POINT_WRITE_BATCH_ROW_MISSING'
    );
  }
  var confirmedIds = Object.create(null);
  confirmedOperations.forEach(function (entry) {
    var operation = entry && (entry.operation || entry);
    if (!operation || operation.batchKey !== batch.batchKey ||
        operation.batchId !== batch.id || operation.rowKey !== rowKey) {
      throw recoveryError(
        'Confirmed mass-operation expiry cleanup does not match its in-memory row.',
        'POINT_WRITE_BATCH_CLEANUP_MISSING'
      );
    }
    confirmedIds[operation.id] = true;
  });
  var remembered = memoryBatchOperations(batch);
  var rowOperations = Object.keys(remembered).map(function (id) {
    return remembered[id];
  }).filter(function (operation) {
    return operation.rowKey === rowKey;
  });
  var unresolved = rowOperations.find(function (operation) {
    return operation.attemptedAt !== null && !confirmedIds[operation.id];
  });
  if (unresolved) {
    throw recoveryError(
      'An attempted mass-operation phase is still unresolved and cannot be expired.',
      'POINT_WRITE_BATCH_EXPIRED_UNRESOLVED'
    );
  }
  Object.keys(confirmedIds).forEach(function (id) {
    if (!rowOperations.some(function (operation) { return operation.id === id; })) {
      throw recoveryError(
        'Confirmed mass-operation expiry cleanup is missing its in-memory phase.',
        'POINT_WRITE_BATCH_CLEANUP_MISSING'
      );
    }
  });
  forgetMemoryBatchRow(batch, rowKey);
  row.status = 'expired';
  row.warning = warning ? String(warning) :
    'Scheduled relinquish deadline elapsed before this row could finish.';
  Object.keys(row.phases || {}).forEach(function (phaseName) {
    row.phases[phaseName].status = 'expired';
    row.phases[phaseName].operationId = null;
  });
  batch.updatedAt = Date.now();
  var completed = Object.keys(batch.rows).every(function (candidateRowKey) {
    return batch.rows[candidateRowKey].status !== 'pending';
  });
  if (completed && holder && holder[key] === batch) delete holder[key];
  return completed;
}

async function markBatchRowSuccess(holder, key, batch, rowKey, warning) {
  try {
    requireActorMatch(batch && batch.actorRid);
  } catch (error) {
    if (isTerminalActorTransition(error)) return false;
    throw error;
  }
  rowKey = String(rowKey || '');
  if (batch.recoveryStorage === 'memory' && isAttendedRecord(batch)) {
    return markMemoryBatchRowSuccess(holder, key, batch, rowKey, warning);
  }
  var outcome;
  try {
    outcome = await withLockedDocument(batch.actorRid, function (document) {
      var durableBatch = document.batches.find(function (candidate) {
        return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
      });
      if (!durableBatch) {
        // This API is called only with authoritative row success. A stale tab
        // must not touch a newer identical-intent generation.
        return { completed: true, receiptIds: [] };
      }
      var row = durableBatch.rows[rowKey];
      if (!row) {
        throw recoveryError('The durable mass-operation row is missing.', 'POINT_WRITE_BATCH_ROW_MISSING');
      }
      row.status = 'success';
      row.warning = warning ? String(warning) : null;
      Object.keys(row.phases || {}).forEach(function (phaseName) {
        row.phases[phaseName].status = 'success';
        row.phases[phaseName].operationId = null;
      });
      document.operations = document.operations.filter(function (operation) {
        return !(operation.batchKey === durableBatch.batchKey &&
          operation.batchId === durableBatch.id && operation.rowKey === rowKey);
      });
      durableBatch.updatedAt = Date.now();
      var completed = Object.keys(durableBatch.rows).every(function (candidateRowKey) {
        return durableBatch.rows[candidateRowKey].status !== 'pending';
      });
      var receiptIds = [];
      if (completed) {
        terminalizeBatch(document, durableBatch, {
          preserveReceipt: true,
          retireOverlapping: true
        });
        receiptIds.push(durableBatch.id);
      }
      return { completed: completed, receiptIds: receiptIds };
    });
  } catch (error) {
    if (isAttendedRecord(batch) && isAttendedStorageFallback(error)) {
      useMemoryRecovery(batch);
      return markMemoryBatchRowSuccess(holder, key, batch, rowKey, warning);
    }
    if (!isCleanupPersistenceError(error)) throw error;
    return false;
  }
  if (outcome.completed && holder && holder[key] && holder[key].batchKey === batch.batchKey &&
      holder[key].id === batch.id) {
    delete holder[key];
  }
  return outcome.completed;
}

async function markBatchRowExpired(holder, key, batch, rowKey, confirmedOperations, warning) {
  try {
    requireActorMatch(batch && batch.actorRid);
  } catch (error) {
    if (isTerminalActorTransition(error)) return false;
    throw error;
  }
  rowKey = String(rowKey || '');
  confirmedOperations = Array.isArray(confirmedOperations) ? confirmedOperations : [];
  if (batch.recoveryStorage === 'memory' && isAttendedRecord(batch)) {
    return markMemoryBatchRowExpired(
      holder, key, batch, rowKey, confirmedOperations, warning
    );
  }
  var outcome;
  try {
    outcome = await withLockedDocument(batch.actorRid, function (document) {
      var durableBatch = document.batches.find(function (candidate) {
      return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
      });
      if (!durableBatch) {
      // Expiry cleanup carries explicit confirmed/unattempted terminal proof.
      // A stale tab must not touch a newer identical-intent generation.
        return { completed: true, receiptIds: [] };
      }
      var row = durableBatch.rows[rowKey];
    if (!row || row.status !== 'pending') {
      throw recoveryError(
        'The pending durable mass-operation row is missing during expiry cleanup.',
        'POINT_WRITE_BATCH_ROW_MISSING'
      );
    }
    var confirmedIds = Object.create(null);
    confirmedOperations.forEach(function (entry) {
      var operation = entry && (entry.operation || entry);
      if (!operation || operation.batchKey !== durableBatch.batchKey ||
          operation.batchId !== durableBatch.id ||
          operation.rowKey !== rowKey) {
        throw recoveryError(
          'Confirmed mass-operation expiry cleanup does not match its durable row.',
          'POINT_WRITE_BATCH_CLEANUP_MISSING'
        );
      }
      var durable = findExactOperation(document, operation);
      if (!durable) {
        throw recoveryError(
          'A confirmed mass-operation phase disappeared during expiry cleanup.',
          'POINT_WRITE_BATCH_CLEANUP_MISSING'
        );
      }
      confirmedIds[durable.id] = true;
    });
    var rowOperations = document.operations.filter(function (operation) {
      return operation.batchKey === durableBatch.batchKey &&
        operation.batchId === durableBatch.id && operation.rowKey === rowKey;
    });
    var unresolved = rowOperations.find(function (operation) {
      return operation.attemptedAt !== null && !confirmedIds[operation.id];
    });
    if (unresolved) {
      throw recoveryError(
        'An attempted mass-operation phase is still unresolved and cannot be expired.',
        'POINT_WRITE_BATCH_EXPIRED_UNRESOLVED'
      );
    }
    Object.keys(confirmedIds).forEach(function (id) {
      if (!rowOperations.some(function (operation) { return operation.id === id; })) {
        throw recoveryError(
          'Confirmed mass-operation expiry cleanup is missing its durable phase.',
          'POINT_WRITE_BATCH_CLEANUP_MISSING'
        );
      }
    });
    document.operations = document.operations.filter(function (operation) {
      return rowOperations.indexOf(operation) === -1;
    });
    row.status = 'expired';
    row.warning = warning ? String(warning) : 'Scheduled relinquish deadline elapsed before this row could finish.';
    Object.keys(row.phases || {}).forEach(function (phaseName) {
      row.phases[phaseName].status = 'expired';
      row.phases[phaseName].operationId = null;
    });
    durableBatch.updatedAt = Date.now();
    var allTerminal = Object.keys(durableBatch.rows).every(function (candidateRowKey) {
      return durableBatch.rows[candidateRowKey].status !== 'pending';
    });
    var receiptIds = [];
    if (allTerminal) {
      terminalizeBatch(document, durableBatch, {
        preserveReceipt: true,
        retireOverlapping: true
      });
      receiptIds.push(durableBatch.id);
    }
      return { completed: allTerminal, receiptIds: receiptIds };
    });
  } catch (error) {
    if (isAttendedRecord(batch) && isAttendedStorageFallback(error)) {
      useMemoryRecovery(batch);
      return markMemoryBatchRowExpired(
        holder, key, batch, rowKey, confirmedOperations, warning
      );
    }
    if (!isCleanupPersistenceError(error)) throw error;
    return false;
  }
  if (outcome.completed && holder && holder[key] && holder[key].batchKey === batch.batchKey &&
      holder[key].id === batch.id) {
    delete holder[key];
  }
  return outcome.completed;
}

async function reloadBatch(batch) {
  requireActorMatch(batch && batch.actorRid);
  if (batch.recoveryStorage === 'memory' && isAttendedRecord(batch)) {
    return publicBatch(batch, true);
  }
  try {
    return await withLockedDocument(batch.actorRid, function (document) {
      var durable = document.batches.find(function (candidate) {
        return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
      });
      if (!durable) {
        durable = document.batchReceipts.find(function (candidate) {
          return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
        });
      }
      return durable ? publicBatch(durable, true) : null;
    }, { readOnly: true });
  } catch (error) {
    if (!isAttendedRecord(batch) || !isAttendedStorageFallback(error)) throw error;
    useMemoryRecovery(batch);
    return publicBatch(batch, true);
  }
}

async function abandonBatch(holder, key, batch) {
  requireActorMatch(batch && batch.actorRid);
  await withLockedDocument(batch.actorRid, function (document) {
    var durable = document.batches.find(function (candidate) {
      return candidate.batchKey === batch.batchKey && candidate.id === batch.id;
    });
    if (!durable) return;
    var unsafe = document.operations.find(function (operation) {
      if (operation.batchKey !== durable.batchKey || operation.batchId !== durable.id ||
          operation.attemptedAt === null) return false;
      var row = durable.rows[operation.rowKey];
      return !row || row.status !== 'success';
    });
    if (unsafe) {
      throw recoveryError(
        'This mass operation contains an attempted write without a proven terminal result and cannot be abandoned.',
        'POINT_WRITE_BATCH_ABANDON_UNSAFE'
      );
    }
    var hasTerminalHistory = Object.keys(durable.rows).some(function (rowKey) {
      return durable.rows[rowKey].status !== 'pending';
    });
    if (hasTerminalHistory) {
      Object.keys(durable.rows).forEach(function (rowKey) {
        var row = durable.rows[rowKey];
        if (row.status !== 'pending') return;
        if (!Object.keys(row.phases).length) {
          throw recoveryError(
            'A partial mass operation with an unseeded row cannot be abandoned safely.',
            'POINT_WRITE_BATCH_ABANDON_UNSAFE'
          );
        }
        row.status = 'expired';
        row.warning = 'Abandoned before transport after another row became terminal.';
        Object.keys(row.phases).forEach(function (phaseName) {
          row.phases[phaseName].status = 'expired';
          row.phases[phaseName].operationId = null;
        });
      });
      durable.updatedAt = Date.now();
      terminalizeBatch(document, durable, {
        preserveReceipt: true,
        retireOverlapping: false,
        semanticEligible: false
      });
      return;
    }
    document.operations = document.operations.filter(function (operation) {
      return operation.batchKey !== durable.batchKey || operation.batchId !== durable.id;
    });
    document.batches = document.batches.filter(function (candidate) {
      return candidate !== durable;
    });
  });
  if (holder && holder[key] && holder[key].batchKey === batch.batchKey &&
      holder[key].id === batch.id) delete holder[key];
}

async function readDocument(actorRid) {
  actorRid = normalizeActorRid(actorRid) || requireActorRid();
  return withLockedDocument(actorRid, function (document) {
    return clone(document);
  }, { readOnly: true });
}

module.exports = {
  ATTENDED_RECOVERY_POLICY: ATTENDED_RECOVERY_POLICY,
  prepare: prepare,
  assertDurable: assertDurable,
  markAttempted: markAttempted,
  complete: complete,
  completeMany: completeMany,
  expireMany: expireMany,
  fail: fail,
  isProvenRetrySafe: isProvenRetrySafe,
  currentActorRid: currentActorRid,
  pointRidForOperation: pointRidForOperation,
  pointRidsForOperation: pointRidsForOperation,
  pointConflictScope: pointConflictScope,
  prepareBatch: prepareBatch,
  seedBatchOperations: seedBatchOperations,
  markBatchRowSuccess: markBatchRowSuccess,
  markBatchRowExpired: markBatchRowExpired,
  reloadBatch: reloadBatch,
  abandonBatch: abandonBatch,
  _test: {
    canonicalValue: canonicalValue,
    stableJson: stableJson,
    operationFingerprint: operationFingerprint,
    operationIntentFingerprint: operationIntentFingerprint,
    recoveryIntentValue: recoveryIntentValue,
    normalizeActorRid: normalizeActorRid,
    storageKey: storageKey,
    lockKey: lockKey,
    emptyDocument: emptyDocument,
    documentIntegrityFingerprint: documentIntegrityFingerprint,
    parseDocument: parseDocument,
    withLockedDocument: withLockedDocument,
    readDocument: readDocument,
    recoveryError: recoveryError
  }
};
